import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Crown, UserPlus, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { MemberNameWithPositions } from '@/components/shared/member-name-with-positions';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { SimpleDonutChart } from '@/components/shared/simple-donut-chart';
import { StatCard } from '@/components/shared/stat-card';
import { VenueSelect } from '@/components/shared/venue-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { SwitchField } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

import {
  audienceFromRecord,
  audienceFromTemplateParams,
  audiencePayload,
  demographicTemplateParams,
  emptyAudience,
  GroupAudienceFields,
  type GroupAudienceValue,
  type GroupGender,
} from './group-audience-fields';
import {
  criteriaFromTemplateParams,
  criteriaPayload,
  emptyCriteria,
  ProfileCriteriaFields,
  type ProfileCriteriaValue,
} from './generational-group-criteria-fields';
import { GroupJoinRequestsPanel } from './group-join-requests-panel';
import { GroupLeadershipPanel } from './group-leadership-panel';
import { GroupLinkedNotice } from './group-linked-notice';

const DEMOGRAPHIC_TEMPLATES = new Set(['gender', 'age_band', 'demographics']);

interface GroupMember {
  id: string;
  memberId: string;
  name: string;
  isLeader: boolean;
  currentTermRoles?: string[];
}

interface GroupInsights {
  memberCount: number;
  leaderCount: number;
  joinedThisMonth: number;
  pendingJoinRequests: number;
  byGender: Array<{ key: string; value: number }>;
  joinTrend: Array<{ key: string; label: string; value: number }>;
}

interface GroupDetail {
  id: string;
  name: string;
  type: 'manual' | 'template';
  category: 'committee' | 'group';
  templateId: string | null;
  templateParams: Record<string, unknown> | null;
  description: string | null;
  isOpen: boolean;
  isActive: boolean;
  meetingVenueId: string | null;
  meetingVenueName: string | null;
  gender: GroupGender | null;
  minAge: number | null;
  maxAge: number | null;
  linkedGroupId: string | null;
  linkedGroupName: string | null;
  linkedGroupType: 'manual' | 'template' | null;
}

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  gender: GroupGender | null;
  dateOfBirth: string | null;
}

const personLabel = (person: Pick<Person, 'firstName' | 'lastName'>): string =>
  `${person.firstName} ${person.lastName}`.trim();

const ageYearsFromDob = (dateOfBirth: string | null): number | null => {
  if (!dateOfBirth) return null;
  const birth = new Date(`${dateOfBirth}T00:00:00.000Z`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - birth.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getUTCDate() < birth.getUTCDate())) {
    age -= 1;
  }
  return age;
};

const matchesAudience = (
  person: Person,
  audience: Pick<GroupDetail, 'gender' | 'minAge' | 'maxAge'>,
): boolean => {
  if (audience.gender && person.gender !== audience.gender) return false;
  if (audience.minAge == null && audience.maxAge == null) return true;
  const age = ageYearsFromDob(person.dateOfBirth);
  if (age == null) return true;
  if (audience.minAge != null && age < audience.minAge) return false;
  if (audience.maxAge != null && age > audience.maxAge) return false;
  return true;
};

export const GroupDetailPage = () => {
  const { groupId = '' } = useParams();
  const { t } = useTranslation('groups');
  const { t: tCommittees } = useTranslation('committees');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);

  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [meetingVenueId, setMeetingVenueId] = useState<string | null>(null);
  const [audience, setAudience] = useState<GroupAudienceValue>(emptyAudience);
  const [criteria, setCriteria] = useState<ProfileCriteriaValue>(emptyCriteria);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [birthdayMonth, setBirthdayMonth] = useState<string | null>(null);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
  const [memberPage, setMemberPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState(25);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(() => new Set());
  const [editTarget, setEditTarget] = useState<GroupMember | null>(null);
  const [editMembershipGroupId, setEditMembershipGroupId] = useState<string | null>(null);
  const [moveTargetGroupId, setMoveTargetGroupId] = useState<string | null>(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  const allowsFeature = useChurchStore((state) => state.allowsFeature);

  const canEditMembers = hasPermission(membership, PERMISSIONS.GROUPS_EDIT);
  const canManageLeaders = hasPermission(membership, PERMISSIONS.GROUPS_MANAGE_LEADERS);
  const canApproveJoin = hasPermission(membership, PERMISSIONS.GROUPS_APPROVE_JOIN);

  const group = useQuery({
    queryKey: ['groups', groupId],
    queryFn: () => api.get<GroupDetail>(`/groups/${groupId}`),
    enabled: Boolean(groupId),
  });

  const insights = useQuery({
    queryKey: ['groups', groupId, 'insights'],
    queryFn: () => api.get<GroupInsights>(`/groups/${groupId}/insights`),
    enabled: Boolean(groupId),
  });

  const groupMembers = useQuery({
    queryKey: ['groups', groupId, 'members', memberPage, memberPageSize, memberSearch],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(memberPage),
        pageSize: String(memberPageSize),
      });
      const q = memberSearch.trim();
      if (q) params.set('q', q);
      return api.get<{
        data: GroupMember[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/groups/${groupId}/members?${params.toString()}`);
    },
    enabled: Boolean(groupId),
  });

  const linkedManualMemberIds = useQuery({
    queryKey: ['groups', group.data?.linkedGroupId, 'member-ids'],
    queryFn: async () => {
      const linkedId = group.data!.linkedGroupId!;
      const ids = new Set<string>();
      let page = 1;
      let totalPages = 1;
      do {
        const response = await api.get<{
          data: GroupMember[];
          page: { totalPages: number };
        }>(`/groups/${linkedId}/members?page=${page}&pageSize=100`);
        for (const row of response.data) ids.add(row.memberId);
        totalPages = response.page.totalPages;
        page += 1;
      } while (page <= totalPages);
      return ids;
    },
    enabled:
      Boolean(groupId) &&
      group.data?.type === 'template' &&
      Boolean(group.data.linkedGroupId) &&
      group.data.linkedGroupType === 'manual',
  });

  const memberIdsForPicker = useQuery({
    queryKey: ['groups', groupId, 'member-ids'],
    queryFn: async () => {
      const ids = new Set<string>();
      let page = 1;
      let totalPages = 1;
      do {
        const response = await api.get<{
          data: GroupMember[];
          page: { totalPages: number };
        }>(`/groups/${groupId}/members?page=${page}&pageSize=100`);
        for (const row of response.data) ids.add(row.memberId);
        totalPages = response.page.totalPages;
        page += 1;
      } while (page <= totalPages);
      return ids;
    },
    enabled: addOpen && Boolean(groupId),
  });

  const departments = useQuery({
    queryKey: ['people', 'departments', 'options'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments'),
    enabled: editOpen && group.data?.templateId === 'department',
  });

  const existingMemberIds = useMemo(
    () => memberIdsForPicker.data ?? new Set<string>(),
    [memberIdsForPicker.data],
  );

  const moveTargetGroups = useQuery({
    queryKey: ['groups', 'move-targets', group.data?.category, editMembershipGroupId],
    queryFn: async () => {
      const excludeGroupId = editMembershipGroupId ?? groupId;
      const params = new URLSearchParams({
        type: 'manual',
        pageSize: '100',
      });
      if (group.data?.category) params.set('category', group.data.category);
      const response = await api.get<{
        data: Array<{ id: string; name: string; isActive: boolean }>;
      }>(`/groups?${params.toString()}`);
      return response.data.filter((row) => row.isActive && row.id !== excludeGroupId);
    },
    enabled: Boolean(editTarget) && Boolean(group.data),
  });

  const openMemberEdit = (row: GroupMember, membershipGroupId: string) => {
    setEditMembershipGroupId(membershipGroupId);
    setEditTarget(row);
  };

  const people = useQuery({
    queryKey: [
      'people',
      'members',
      'group-picker',
      memberQuery,
      group.data?.gender ?? null,
    ],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '50', status: 'active' });
      if (memberQuery) params.set('q', memberQuery);
      if (group.data?.gender) params.set('gender', group.data.gender);
      return api.get<{ data: Person[] }>(`/people/members?${params.toString()}`);
    },
    enabled: addOpen,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!group.data || !editOpen) return;
    setName(group.data.name);
    setDescription(group.data.description ?? '');
    setIsOpen(group.data.isOpen);
    setIsActive(group.data.isActive);
    setMeetingVenueId(group.data.meetingVenueId);
    setPreviewCount(null);

    if (group.data.type === 'template') {
      if (group.data.templateId === 'profile') {
        setCriteria(criteriaFromTemplateParams(group.data.templateParams));
      } else if (
        group.data.templateId &&
        DEMOGRAPHIC_TEMPLATES.has(group.data.templateId)
      ) {
        setAudience(audienceFromTemplateParams(group.data.templateParams));
      } else if (group.data.templateId === 'department') {
        const params = group.data.templateParams;
        setDepartmentId(typeof params?.departmentId === 'string' ? params.departmentId : null);
      } else if (group.data.templateId === 'birthday_month') {
        const params = group.data.templateParams;
        setBirthdayMonth(
          typeof params?.month === 'number' ? String(params.month) : null,
        );
      }
      return;
    }

    setAudience(
      audienceFromRecord({
        gender: group.data.gender,
        minAge: group.data.minAge,
        maxAge: group.data.maxAge,
      }),
    );
  }, [group.data, editOpen]);

  useEffect(() => {
    if (!addOpen) return;
    setPickedId(null);
    setSelectedPeople([]);
    setMemberQuery('');
  }, [addOpen]);

  useEffect(() => {
    if (editTarget) return;
    setEditMembershipGroupId(null);
    setMoveTargetGroupId(null);
    setRemoveConfirmOpen(false);
  }, [editTarget]);

  const selectedIds = useMemo(
    () => new Set(selectedPeople.map((person) => person.id)),
    [selectedPeople],
  );

  const memberOptions = useMemo(() => {
    const criteria = {
      gender: group.data?.gender ?? null,
      minAge: group.data?.minAge ?? null,
      maxAge: group.data?.maxAge ?? null,
    };
    return (people.data?.data ?? [])
      .filter(
        (person) =>
          !existingMemberIds.has(person.id) &&
          !selectedIds.has(person.id) &&
          matchesAudience(person, criteria),
      )
      .map((person) => ({
        id: person.id,
        label: personLabel(person),
      }));
  }, [
    existingMemberIds,
    group.data?.gender,
    group.data?.maxAge,
    group.data?.minAge,
    people.data?.data,
    selectedIds,
  ]);

  const invalidateGroupMembership = () => {
    void queryClient.invalidateQueries({ queryKey: ['groups', groupId] });
    void queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'members'] });
    void queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'member-ids'] });
    void queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'insights'] });
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const invalidateMembershipGroup = (membershipGroupId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['groups', membershipGroupId] });
    void queryClient.invalidateQueries({ queryKey: ['groups', membershipGroupId, 'members'] });
    void queryClient.invalidateQueries({ queryKey: ['groups', membershipGroupId, 'member-ids'] });
    void queryClient.invalidateQueries({ queryKey: ['groups', membershipGroupId, 'insights'] });
    invalidateGroupMembership();
  };

  const save = useMutation({
    mutationFn: () => {
      const base = {
        name,
        description: description.trim() || null,
        isActive,
        meetingVenueId,
      };

      if (group.data?.type === 'manual') {
        return api.patch<GroupDetail>(`/groups/${groupId}`, {
          ...base,
          isOpen,
          ...audiencePayload(audience),
        });
      }

      const templateId = group.data?.templateId;
      let templateParams: Record<string, unknown> | undefined;
      if (templateId === 'profile') {
        templateParams = criteriaPayload(criteria);
      } else if (templateId && DEMOGRAPHIC_TEMPLATES.has(templateId)) {
        templateParams = demographicTemplateParams(templateId, audience);
      } else if (templateId === 'department' && departmentId) {
        templateParams = { departmentId };
      } else if (templateId === 'birthday_month' && birthdayMonth) {
        templateParams = { month: Number(birthdayMonth) };
      }

      return api.patch<GroupDetail>(`/groups/${groupId}`, {
        ...base,
        ...(templateParams !== undefined ? { templateParams } : {}),
      });
    },
    onSuccess: () => {
      toast.success(t('saved'));
      setEditOpen(false);
      invalidateGroupMembership();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const addMembers = useMutation({
    mutationFn: async () => {
      const ids = selectedPeople.map((person) => person.id);
      let added = 0;
      for (const memberId of ids) {
        try {
          await api.post(`/groups/${groupId}/members`, { memberId });
          added += 1;
        } catch (err) {
          if (err instanceof ApiError && err.code === 'GROUP_MEMBER_EXISTS') continue;
          throw err;
        }
      }
      return added;
    },
    onSuccess: (added) => {
      toast.success(t('addMembers.saved', { count: added }));
      setAddOpen(false);
      invalidateGroupMembership();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeMember = useMutation({
    mutationFn: ({ memberId, membershipGroupId }: { memberId: string; membershipGroupId: string }) =>
      api.delete(`/groups/${membershipGroupId}/members/${memberId}`),
    onSuccess: (_data, { membershipGroupId }) => {
      toast.success(t('editMember.removed', { name: editTarget?.name ?? '' }));
      setEditTarget(null);
      setRemoveConfirmOpen(false);
      invalidateMembershipGroup(membershipGroupId);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const moveMember = useMutation({
    mutationFn: async ({
      memberId,
      membershipGroupId,
      targetGroupId,
    }: {
      memberId: string;
      membershipGroupId: string;
      targetGroupId: string;
    }) => {
      await api.post(`/groups/${membershipGroupId}/members/${memberId}/move`, { targetGroupId });
      const targetName =
        moveTargetGroups.data?.find((row) => row.id === targetGroupId)?.name ?? '';
      return { targetName, membershipGroupId };
    },
    onSuccess: ({ targetName, membershipGroupId }) => {
      toast.success(t('editMember.moved', { name: editTarget?.name ?? '', groupName: targetName }));
      setEditTarget(null);
      invalidateMembershipGroup(membershipGroupId);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const resync = useMutation({
    mutationFn: () => api.post(`/groups/${groupId}/evaluate`),
    onSuccess: () => {
      toast.success(t('resynced'));
      setMemberPage(1);
      setSelectedMemberIds(new Set());
      invalidateGroupMembership();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const addToLinkedManual = useMutation({
    mutationFn: async ({
      linkedId,
      memberIds,
    }: {
      linkedId: string;
      memberIds: string[];
    }) => {
      let added = 0;
      for (const memberId of memberIds) {
        try {
          await api.post(`/groups/${linkedId}/members`, { memberId });
          added += 1;
        } catch (err) {
          if (err instanceof ApiError && err.code === 'GROUP_MEMBER_EXISTS') continue;
          throw err;
        }
      }
      return added;
    },
    onSuccess: (added, { memberIds, linkedId }) => {
      toast.success(t('linked.addToManualSaved', { count: added }));
      setSelectedMemberIds((current) => {
        const next = new Set(current);
        for (const memberId of memberIds) next.delete(memberId);
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['groups', linkedId, 'member-ids'] });
      void queryClient.invalidateQueries({ queryKey: ['groups', linkedId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['groups', linkedId] });
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
      void queryClient.invalidateQueries({ queryKey: ['committees'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const previewCriteria = useMutation({
    mutationFn: () =>
      api.post<{ count: number }>('/groups/templates/preview', {
        templateId: 'profile',
        templateParams: criteriaPayload(criteria),
      }),
    onSuccess: (result) => {
      setPreviewCount(result.count);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<GroupMember>[] = [
    {
      id: 'name',
      header: t('memberColumns.name'),
      cell: (row) => (
        <MemberNameWithPositions
          memberId={row.memberId}
          name={row.name}
          positions={row.currentTermRoles}
        />
      ),
    },
    {
      id: 'role',
      header: t('memberColumns.role'),
      cell: (row) =>
        row.isLeader ? <Badge variant="brand">{t('leader')}</Badge> : t('member'),
    },
  ];

  if (!group.data) {
    return (
      <div>
        <PageHeader
          title={t('title')}
          breadcrumbs={[
            { label: t('title'), to: '/groups' },
            { label: tCommon('state.loading') },
          ]}
        />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
      </div>
    );
  }

  const canAddMembers =
    canEditMembers && group.data.type === 'manual' && group.data.isActive;

  const showLinkedManualActions =
    canEditMembers &&
    group.data.type === 'template' &&
    Boolean(group.data.linkedGroupId) &&
    group.data.linkedGroupType === 'manual' &&
    group.data.isActive;

  const linkedGroupId = group.data.linkedGroupId;
  const linkedGroupName = group.data.linkedGroupName ?? group.data.name;

  const listPath =
    group.data.category === 'committee'
      ? '/committees'
      : group.data.type === 'manual'
        ? '/groups'
        : '/groups/auto-filled';
  const listLabel =
    group.data.category === 'committee' ? tCommittees('title') : t('title');
  const canResync =
    group.data.type === 'template' &&
    allowsFeature('smart_groups') &&
    hasPermission(membership, PERMISSIONS.GROUPS_MANAGE_TEMPLATES);

  const pendingJoinRequests = insights.data?.pendingJoinRequests ?? 0;
  const showJoinRequestsTab =
    canApproveJoin &&
    group.data.type === 'manual' &&
    (group.data.isOpen || pendingJoinRequests > 0);

  return (
    <div>
      <PageHeader
        title={group.data.name}
        description={
          group.data.description ??
          (group.data.meetingVenueName
            ? group.data.meetingVenueName
            : t(`type.${group.data.type}`))
        }
        tipId="groups.leadershipScope"
        breadcrumbs={[
          { label: listLabel, to: listPath },
          { label: group.data.name },
        ]}
        meta={
          !group.data.isActive ? (
            <Badge variant="neutral">{t('statusInactive')}</Badge>
          ) : null
        }
        headerActions={
          <div className="flex justify-end gap-2">
            {canResync ? (
              <Button
                size="sm"
                variant="outline"
                loading={resync.isPending}
                onClick={() => resync.mutate()}
              >
                {t('resync')}
              </Button>
            ) : null}
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              {tCommon('action.edit')}
            </Button>
            {canAddMembers ? (
              <Button size="sm" onClick={() => setAddOpen(true)}>
                {t('addMembers.action')}
              </Button>
            ) : null}
          </div>
        }
      />

      {group.data.linkedGroupId ? (
        <div className="mb-4">
          <GroupLinkedNotice
            linkedGroupId={group.data.linkedGroupId}
            linkedGroupName={group.data.linkedGroupName}
            linkedGroupType={group.data.linkedGroupType}
            currentType={group.data.type}
          />
        </div>
      ) : null}

      <CollapsibleInsights
        summary={[
          {
            label: t('insights.members'),
            value: insights.data ? formatNumber(insights.data.memberCount) : '…',
          },
          {
            label: t('insights.leaders'),
            value: insights.data ? formatNumber(insights.data.leaderCount) : '…',
          },
          {
            label: t('insights.joinedThisMonth'),
            value: insights.data ? formatNumber(insights.data.joinedThisMonth) : '…',
          },
          {
            label: t('insights.pendingRequests'),
            value: insights.data ? formatNumber(insights.data.pendingJoinRequests) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={t('insights.members')}
            value={insights.data ? formatNumber(insights.data.memberCount) : undefined}
            icon={<Users className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('insights.leaders')}
            value={insights.data ? formatNumber(insights.data.leaderCount) : undefined}
            icon={<Crown className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('insights.joinedThisMonth')}
            value={insights.data ? formatNumber(insights.data.joinedThisMonth) : undefined}
            icon={<UserPlus className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('insights.pendingRequests')}
            value={insights.data ? formatNumber(insights.data.pendingJoinRequests) : undefined}
            icon={<ClipboardList className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard
            title={t('insights.byGender')}
            loading={insights.isLoading}
            hasData={(insights.data?.byGender.length ?? 0) > 0}
            empty={{ title: t('insights.breakdownEmpty') }}
            height={180}
          >
            <SimpleDonutChart
              data={(insights.data?.byGender ?? []).map((row) => ({
                label: t(`insights.gender.${row.key}`, { defaultValue: row.key }),
                value: row.value,
              }))}
              formatValue={(value) => formatNumber(value)}
              centerLabel={t('insights.members')}
              centerValue={
                insights.data ? formatNumber(insights.data.memberCount) : undefined
              }
              size={148}
            />
          </ChartCard>
          <ChartCard
            title={t('insights.joinTrend')}
            loading={insights.isLoading}
            hasData={(insights.data?.joinTrend.some((row) => row.value > 0) ?? false)}
            empty={{ title: t('insights.trendEmpty') }}
            height={180}
          >
            <SimpleBarChart
              data={(insights.data?.joinTrend ?? []).map((row) => ({
                label: row.label,
                value: row.value,
              }))}
              formatValue={(value) => formatNumber(value)}
              height={160}
            />
          </ChartCard>
        </div>
      </CollapsibleInsights>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">{t('tabs.members')}</TabsTrigger>
          {showJoinRequestsTab ? (
            <TabsTrigger value="join-requests" className="inline-flex items-center gap-2">
              {t('tabs.joinRequests')}
              {pendingJoinRequests > 0 ? (
                <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-brand px-1.5 py-0.5 text-caption font-medium text-on-brand">
                  {pendingJoinRequests > 99 ? '99+' : pendingJoinRequests}
                </span>
              ) : null}
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="leadership">{t('tabs.leadership')}</TabsTrigger>
        </TabsList>
        <TabsContent value="members">
          {group.data.type === 'template' ? (
            <div className="mb-4">
              <SearchInput
                value={memberSearch}
                onChange={(value) => {
                  setMemberSearch(value);
                  setMemberPage(1);
                }}
                placeholder={t('membersSearch')}
                clearLabel={tCommon('search.clear')}
                className="max-w-md"
                aria-label={t('membersSearch')}
              />
            </div>
          ) : null}

          {showLinkedManualActions && selectedMemberIds.size > 0 ? (
            <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
              <span className="text-body-sm text-fg-secondary">
                {t('linked.addSelectedCount', { count: selectedMemberIds.size })}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSelectedMemberIds(new Set())}
              >
                {tCommon('action.clearSelection')}
              </Button>
              <Button
                size="sm"
                loading={addToLinkedManual.isPending}
                disabled={!linkedGroupId}
                onClick={() => {
                  if (!linkedGroupId) return;
                  addToLinkedManual.mutate({
                    linkedId: linkedGroupId,
                    memberIds: [...selectedMemberIds],
                  });
                }}
              >
                {t('linked.addSelectedToManual', { name: linkedGroupName })}
              </Button>
            </div>
          ) : null}

          <DataTable
            rows={groupMembers.data?.data ?? []}
            columns={columns}
            rowKey={(row) => row.memberId}
            loading={groupMembers.isLoading}
            empty={
              memberSearch.trim() ? (
                <p className="text-body-sm text-fg-secondary">{t('membersSearchEmpty')}</p>
              ) : (
                <div className="flex flex-col items-start gap-3">
                  <p className="text-body-sm text-fg-secondary">{t('members', { count: 0 })}</p>
                  {canAddMembers ? (
                    <Button size="sm" onClick={() => setAddOpen(true)}>
                      {t('addMembers.action')}
                    </Button>
                  ) : null}
                </div>
              )
            }
            selection={
              showLinkedManualActions
                ? {
                    selected: selectedMemberIds,
                    onChange: setSelectedMemberIds,
                    selectAllLabel: t('linked.selectMembers'),
                    selectRowLabel: t('linked.selectMember'),
                  }
                : undefined
            }
            actionsHeader={
              canAddMembers || showLinkedManualActions ? tCommon('table.actions') : undefined
            }
            rowActions={
              canAddMembers
                ? (row) => (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        openMemberEdit(row, groupId);
                      }}
                    >
                      {tCommon('action.edit')}
                    </Button>
                  )
                : showLinkedManualActions
                  ? (row) => {
                      const alreadyInManual =
                        linkedManualMemberIds.data?.has(row.memberId) ?? false;
                      if (alreadyInManual && linkedGroupId) {
                        return (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(event) => {
                              event.stopPropagation();
                              openMemberEdit(row, linkedGroupId);
                            }}
                          >
                            {tCommon('action.edit')}
                          </Button>
                        );
                      }
                      return (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={addToLinkedManual.isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!linkedGroupId) return;
                            addToLinkedManual.mutate({
                              linkedId: linkedGroupId,
                              memberIds: [row.memberId],
                            });
                          }}
                        >
                          {t('linked.addToManual', { name: linkedGroupName })}
                        </Button>
                      );
                    }
                  : undefined
            }
          />
          {groupMembers.data?.page && groupMembers.data.page.totalPages > 1 ? (
            <Pagination
              className="mt-4"
              page={groupMembers.data.page.page}
              pageSize={groupMembers.data.page.pageSize}
              totalItems={groupMembers.data.page.totalItems}
              totalPages={groupMembers.data.page.totalPages}
              onPageChange={setMemberPage}
              onPageSizeChange={(next) => {
                setMemberPageSize(next);
                setMemberPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) =>
                  tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </TabsContent>
        {showJoinRequestsTab ? (
          <TabsContent value="join-requests">
            <GroupJoinRequestsPanel
              groupId={group.data.id}
              canManage={canApproveJoin}
              category={group.data.category}
            />
          </TabsContent>
        ) : null}
        <TabsContent value="leadership">
          <GroupLeadershipPanel groupId={group.data.id} canManage={canManageLeaders} />
        </TabsContent>
      </Tabs>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent
          size={group.data.type === 'template' ? 'xl' : undefined}
          closeLabel={tCommon('action.close')}
          className="overflow-visible"
        >
          <DialogHeader>
            <DialogTitle>{tCommon('action.edit')}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <DialogBody contentClassName="flex flex-col gap-6">
              <div className="flex flex-col gap-1.5">
                <Label>{t('name')}</Label>
                <Input value={name} onChange={(event) => setName(event.target.value)} required />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('descriptionField')}</Label>
                <Textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('meetingVenue')}</Label>
                <VenueSelect
                  value={meetingVenueId}
                  onChange={setMeetingVenueId}
                  popoverPortal="dialog"
                />
              </div>
              {group.data.type === 'manual' ? (
                <SwitchField
                  id="group-open"
                  label={t('open')}
                  checked={isOpen}
                  onCheckedChange={setIsOpen}
                />
              ) : null}
              <SwitchField
                id="group-active"
                label={t('active')}
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              {group.data.type === 'manual' ? (
                <>
                  {group.data.linkedGroupId ? (
                    <GroupLinkedNotice
                      linkedGroupId={group.data.linkedGroupId}
                      linkedGroupName={group.data.linkedGroupName}
                      linkedGroupType={group.data.linkedGroupType}
                      currentType={group.data.type}
                    />
                  ) : null}
                  <GroupAudienceFields value={audience} onChange={setAudience} />
                </>
              ) : null}
              {group.data.type === 'template' && group.data.templateId === 'profile' ? (
                <>
                  {group.data.linkedGroupId ? (
                    <GroupLinkedNotice
                      linkedGroupId={group.data.linkedGroupId}
                      linkedGroupName={group.data.linkedGroupName}
                      linkedGroupType={group.data.linkedGroupType}
                      currentType={group.data.type}
                    />
                  ) : null}
                  <ProfileCriteriaFields
                    value={criteria}
                    onChange={(next) => {
                      setCriteria(next);
                      setPreviewCount(null);
                    }}
                  />
                  {previewCount != null ? (
                    <p className="text-body-sm text-fg-secondary">
                      {t('criteria.previewCount', { count: previewCount })}
                    </p>
                  ) : null}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      loading={previewCriteria.isPending}
                      onClick={() => previewCriteria.mutate()}
                    >
                      {t('criteria.preview')}
                    </Button>
                  </div>
                </>
              ) : null}
              {group.data.type === 'template' &&
              group.data.templateId &&
              DEMOGRAPHIC_TEMPLATES.has(group.data.templateId) ? (
                <GroupAudienceFields
                  value={audience}
                  onChange={setAudience}
                  showGender={
                    group.data.templateId === 'gender' ||
                    group.data.templateId === 'demographics'
                  }
                  showAge={
                    group.data.templateId === 'age_band' ||
                    group.data.templateId === 'demographics'
                  }
                />
              ) : null}
              {group.data.type === 'template' && group.data.templateId === 'department' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('criteria.fields.departmentId')}</Label>
                  <Listbox
                    value={departmentId}
                    onChange={setDepartmentId}
                    options={(departments.data?.data ?? []).map((department) => ({
                      value: department.id,
                      label: department.name,
                    }))}
                    placeholder={t('criteria.departmentPlaceholder')}
                    popoverPortal="dialog"
                  />
                </div>
              ) : null}
              {group.data.type === 'template' && group.data.templateId === 'birthday_month' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('criteria.fields.birthdayMonth')}</Label>
                  <Listbox
                    value={birthdayMonth}
                    onChange={setBirthdayMonth}
                    options={Array.from({ length: 12 }, (_, index) => {
                      const month = String(index + 1);
                      return {
                        value: month,
                        label: t(`criteria.months.${month}`),
                      };
                    })}
                    popoverPortal="dialog"
                  />
                </div>
              ) : null}
            </DialogBody>
            <div className="flex justify-end gap-2 px-6 pb-6">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending}>
                {t('save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('addMembers.title')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex min-h-0 flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (selectedPeople.length === 0) return;
              addMembers.mutate();
            }}
          >
            <DialogBody>
              <div className="flex flex-col gap-3">
                <p className="text-body-sm text-fg-secondary">{t('addMembers.description')}</p>
                <div className="flex flex-col gap-1.5">
                  <Label>{t('addMembers.people')}</Label>
                  <EntityCombobox
                    value={pickedId}
                    onChange={(id) => {
                      if (!id) {
                        setPickedId(null);
                        return;
                      }
                      const person = people.data?.data.find((row) => row.id === id);
                      if (!person || existingMemberIds.has(id) || selectedIds.has(id)) {
                        setPickedId(null);
                        return;
                      }
                      setSelectedPeople((current) => [...current, person]);
                      setPickedId(null);
                    }}
                    options={memberOptions}
                    onQueryChange={setMemberQuery}
                    loading={people.isFetching}
                    placeholder={t('addMembers.placeholder')}
                    emptyMessage={t('addMembers.empty')}
                    clearLabel={tCommon('action.clearSelection')}
                    popoverPortal="dialog"
                  />
                </div>
                {selectedPeople.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {selectedPeople.map((person) => (
                      <li key={person.id}>
                        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-caption text-fg-secondary">
                          {personLabel(person)}
                          <button
                            type="button"
                            className="rounded-full p-0.5 text-fg-muted hover:text-fg"
                            aria-label={t('addMembers.remove', { name: personLabel(person) })}
                            onClick={() =>
                              setSelectedPeople((current) =>
                                current.filter((row) => row.id !== person.id),
                              )
                            }
                          >
                            <X className="size-3" aria-hidden />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </DialogBody>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={addMembers.isPending}
                disabled={selectedPeople.length === 0}
              >
                {t('addMembers.confirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('editMember.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody contentClassName="flex flex-col gap-6">
            <p className="text-body-sm text-fg-secondary">
              {editMembershipGroupId && editMembershipGroupId !== groupId
                ? t('editMember.descriptionManualGroup', {
                    name: editTarget?.name ?? '',
                    groupName: linkedGroupName,
                  })
                : t('editMember.description', { name: editTarget?.name ?? '' })}
            </p>
            <div className="flex flex-col gap-1.5">
              <Label>{t('editMember.moveTo')}</Label>
              <Listbox
                value={moveTargetGroupId}
                onChange={setMoveTargetGroupId}
                options={(moveTargetGroups.data ?? []).map((row) => ({
                  value: row.id,
                  label: row.name,
                }))}
                placeholder={t('editMember.moveToPlaceholder')}
                popoverPortal="dialog"
              />
            </div>
          </DialogBody>
          <div className="flex flex-wrap justify-end gap-2 px-6 pb-6">
            <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setRemoveConfirmOpen(true)}
            >
              {t('editMember.remove')}
            </Button>
            <Button
              loading={moveMember.isPending}
              disabled={!moveTargetGroupId || !editMembershipGroupId}
              onClick={() => {
                if (!editTarget || !moveTargetGroupId || !editMembershipGroupId) return;
                moveMember.mutate({
                  memberId: editTarget.memberId,
                  membershipGroupId: editMembershipGroupId,
                  targetGroupId: moveTargetGroupId,
                });
              }}
            >
              {t('editMember.move')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeConfirmOpen}
        onOpenChange={setRemoveConfirmOpen}
        title={t('editMember.removeTitle', { name: editTarget?.name ?? '' })}
        description={t('editMember.removeDescription')}
        confirmLabel={t('editMember.removeConfirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeMember.isPending}
        closeLabel={tCommon('action.close')}
        onConfirm={() => {
          if (editTarget && editMembershipGroupId) {
            removeMember.mutate({
              memberId: editTarget.memberId,
              membershipGroupId: editMembershipGroupId,
            });
          }
        }}
      />
    </div>
  );
};
